import { useCallback, useEffect, useRef, useState } from 'react';
import { Toast } from 'antd-mobile';
import { useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import i18n from '../../../i18n';
import { whatsappApi, WhatsAppAuthMode, WhatsAppBindingDraft, WhatsAppConnectionAttemptSnapshot } from '../../../api/whatsapp';
import { ApiEnvelope } from '../../../api/types';
import { BindAccountFormValues, ProfileWhatsAppAccountCreateState } from './types';

interface RequestErrorShape {
  response?: {
    data?: {
      error?: {
        message?: string;
      };
    };
  };
}

function readErrorMessage(error: RequestErrorShape, fallback: string): string {
  return error.response?.data?.error?.message || fallback;
}

export function useProfileWhatsAppAccountCreateModel() {
  const navigate = useNavigate();
  const [state, setState] = useState<ProfileWhatsAppAccountCreateState>({
    submitting: false,
    connecting: false,
    authMode: 'pairing_code',
    attempt: null,
    qrImage: null,
    targetPhoneNumber: null,
    errorMessage: '',
    pendingConfirm: null,
  });
  const draftRef = useRef<WhatsAppBindingDraft | null>(null);
  const completedRef = useRef(false);

  // Step 1: form submit only opens the confirm-phone modal.
  // The actual API call is deferred until the user clicks "confirm".
  const submit = useCallback(async (values: BindAccountFormValues) => {
    setState((previous) => ({
      ...previous,
      pendingConfirm: {
        ...values,
        phone_number: values.phone_number.trim(),
      },
    }));
  }, []);

  // Step 2: user confirmed the phone in modal -> create draft + start connection.
  const confirmSubmit = useCallback(async () => {
    const pending = state.pendingConfirm;
    if (!pending) {
      return;
    }
    setState((previous) => ({ ...previous, submitting: true, errorMessage: '' }));
    try {
      const response = await whatsappApi.createMyBindingDraft({
        phone_number: pending.phone_number,
        alias: pending.alias?.trim() || undefined,
        set_default: pending.set_default,
      }) as { data: ApiEnvelope<WhatsAppBindingDraft> };
      draftRef.current = response.data.data;
      const attemptResponse = await whatsappApi.connectMyBindingDraft(response.data.data.id, 'pairing_code') as {
        data: ApiEnvelope<WhatsAppConnectionAttemptSnapshot>;
      };
      setState((previous) => ({
        ...previous,
        connecting: true,
        authMode: 'pairing_code',
        attempt: attemptResponse.data.data,
        targetPhoneNumber: response.data.data.requested_phone_number,
        pendingConfirm: null,
      }));
    } catch (error) {
      Toast.show({
        icon: 'fail',
        content: readErrorMessage(error as RequestErrorShape, i18n.t('profile:waAccounts.bindFailed')),
      });
    } finally {
      setState((previous) => ({ ...previous, submitting: false }));
    }
  }, [state.pendingConfirm]);

  // Step 2 (alt): user clicked "edit number" -> simply close the modal.
  const cancelConfirm = useCallback(() => {
    setState((previous) => ({ ...previous, pendingConfirm: null }));
  }, []);

  const resetDraft = useCallback(async () => {
    const draftId = draftRef.current?.id;
    const attemptId = state.attempt?.id;
    const attemptStatus = state.attempt?.status || '';
    setState((previous) => ({ ...previous, submitting: true, errorMessage: '' }));
    try {
      if (
        attemptId &&
        ['created', 'queued', 'runtime_allocated', 'client_starting', 'qr_pending', 'pairing_code_pending', 'authenticating', 'verifying_identity'].includes(attemptStatus)
      ) {
        await whatsappApi.cancelMyConnectionAttempt(attemptId);
      }
      if (draftId) {
        await whatsappApi.cancelMyBindingDraft(draftId);
      }
    } catch {
      // The draft or attempt may already be completed, expired, or cancelled.
    } finally {
      draftRef.current = null;
      completedRef.current = false;
      setState({
        submitting: false,
        connecting: false,
        authMode: 'pairing_code',
        attempt: null,
        qrImage: null,
        targetPhoneNumber: null,
        errorMessage: '',
        pendingConfirm: null,
      });
    }
  }, [state.attempt?.id, state.attempt?.status]);

  const switchMode = useCallback(async (mode: WhatsAppAuthMode) => {
    if (!draftRef.current) {
      return;
    }
    setState((previous) => ({ ...previous, connecting: true, authMode: mode, qrImage: null, errorMessage: '' }));
    try {
      const response = await whatsappApi.connectMyBindingDraft(draftRef.current.id, mode) as {
        data: ApiEnvelope<WhatsAppConnectionAttemptSnapshot>;
      };
      setState((previous) => ({ ...previous, attempt: response.data.data }));
    } catch (error) {
      setState((previous) => ({
        ...previous,
        errorMessage: readErrorMessage(error as RequestErrorShape, i18n.t('profile:waAccounts.connectFailed')),
      }));
    }
  }, []);

  useEffect(() => {
    const attemptId = state.attempt?.id;
    if (!attemptId || !state.connecting || completedRef.current) {
      return;
    }
    let stopped = false;
    const tick = async () => {
      try {
        const response = await whatsappApi.getMyConnectionAttempt(attemptId) as {
          data: ApiEnvelope<WhatsAppConnectionAttemptSnapshot>;
        };
        const attempt = response.data.data;
        const qrImage = attempt.auth_payload.qr
          ? await QRCode.toDataURL(attempt.auth_payload.qr, { width: 240, margin: 2 })
          : null;
        if (stopped) {
          return;
        }
        if (attempt.replacement_attempt) {
          setState((previous) => ({
            ...previous,
            attempt: attempt.replacement_attempt || null,
            qrImage: null,
            errorMessage: '',
          }));
          return;
        }
        setState((previous) => ({ ...previous, attempt, qrImage }));
        if (attempt.status === 'ready') {
          completedRef.current = true;
          Toast.show({ icon: 'success', content: i18n.t('profile:waAccounts.bindSuccess') });
          navigate('/profile/whatsapp-accounts', { replace: true });
          return;
        }
        if (attempt.status === 'failed' || attempt.status === 'expired' || attempt.status === 'cancelled') {
          const failureMessage = attempt.failure_code === 'PHONE_MISMATCH'
            ? i18n.t('profile:waAccounts.bindPhoneMismatch')
            : attempt.failure_message || i18n.t('profile:waAccounts.connectFailed');
          setState((previous) => ({
            ...previous,
            connecting: false,
            errorMessage: failureMessage,
          }));
          return;
        }
      } catch (error) {
        if (!stopped) {
          setState((previous) => ({
            ...previous,
            errorMessage: readErrorMessage(error as RequestErrorShape, i18n.t('profile:waAccounts.connectFailed')),
          }));
        }
      }
      if (!stopped) {
        window.setTimeout(tick, 2000);
      }
    };
    void tick();
    return () => {
      stopped = true;
    };
  }, [navigate, state.attempt?.id, state.connecting]);

  return {
    state,
    submit,
    confirmSubmit,
    cancelConfirm,
    switchMode,
    resetDraft,
  };
}
